<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  GpxMap,
  GpxMapBasemapToggle,
  GpxMapFitBoundsControl,
  GpxMapZoomControl,
  useMapInteractions,
} from '@gpx-spa/ui';
import ProgressSpinner from 'primevue/progressspinner';
import { createLeafletMapProvider } from '@gpx-spa/geo-adapters';
import { latLngBoundsFromLngLatCoordinates } from '@gpx-spa/geo-domain';
import type { LatLngBounds, MapProvider } from '@gpx-spa/geo-domain';
import { useOperationalMapData } from '@/composables/useOperationalMapData';
import { useConvoyLiveMap } from '@/composables/useConvoyLiveMap';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import {
  APP_MAP_TILE_OPTIONS,
  APP_MAP_TILE_URL,
  APP_SATELLITE_BASEMAP,
  getAppMapTileAttribution,
  isMapAttributionVisible,
} from '@/utils/app-map-tiles';
import { COMPOSER_MAP_DEFAULT_CENTER } from '@/utils/shipment-map-constants';
import { createShipmentListMapInteractions } from '@/map/operational/create-shipment-list-map-interactions';
import {
  buildShipmentListMapPayload,
  buildShipmentsById,
} from '@/map/operational/shipment-list-map-layers';
import {
  PVT_SPINNER_STROKE_WIDTH,
  PVT_SPINNER_STYLE_OVERLAY,
} from '@/constants/progress-spinner';
import { afterNextBrowserPaint } from '@/utils/after-next-browser-paint';
import { useMapBasemapModeStore } from '@/stores/useMapBasemapModeStore';
import { usePersistedMapBasemap } from '@/composables/usePersistedMapBasemap';
import ShipmentListMapLegend from './ShipmentListMapLegend.vue';
import ConvoyGroupHoverOverlay from './ConvoyGroupHoverOverlay.vue';

const LAYER_ENDPOINTS_ID = 'shipments-list/endpoints';
const LAYER_CONVOY_ID = 'shipments-list/convoy';
const LAYER_CONVOY_SPIDERFY_ID = 'shipments-list/convoy-spiderfy';

const ENDPOINT_CLUSTER_OPTIONS = {
  cluster: {
    icon: 'donut' as const,
    radius: 60,
    maxZoom: 14,
  },
  arc: {
    minSegmentMeters: 50,
  },
};

const props = withDefaults(
  defineProps<{
    accountId: number;
    shipments: V2ShipmentDetail[];
    loading?: boolean;
  }>(),
  {
    shipments: () => [],
    loading: false,
  },
);

const router = useRouter();

const { positionsQuery } = useOperationalMapData(
  () => props.accountId,
  () => props.shipments,
  {
    tracksFetchEnabled: false,
    routesFetchEnabled: false,
    positionsFetchEnabled: true,
  },
);

const {
  expandedShipmentIds,
  resetExpandedShipments,
  livePaint,
  markers: convoyMarkers,
  convoyInteractionFragment,
  groupHoverTrackersFromHit,
} = useConvoyLiveMap({
  shipments: () => props.shipments,
  positions: () => positionsQuery.data.value ?? [],
});

const hiddenStatusKeys = ref<Set<string>>(new Set());

const mapReady = ref(false);
const layersPaintEnabled = ref(false);

const userAdjustedMapView = ref(false);
const treatNextMoveEndAsProgrammatic = ref(false);
const mapMoveIntentTrackingEnabled = ref(false);

let offMapMoveEnd: (() => void) | undefined;

const mapBasemapStore = useMapBasemapModeStore();

const mapProvider: MapProvider = createLeafletMapProvider({
  basemap: {
    streets: { rasterUrl: APP_MAP_TILE_URL },
    satellite: APP_SATELLITE_BASEMAP,
    defaultMode: mapBasemapStore.basemapMode,
  },
  getTileAttribution: getAppMapTileAttribution,
  tileLayerOptions: { ...APP_MAP_TILE_OPTIONS },
  defaultCenter: COMPOSER_MAP_DEFAULT_CENTER,
  defaultZoom: 4,
  showAttribution: isMapAttributionVisible(),
  zoomControl: false,
  preferWebGL: true,
});

const { basemapMode } = usePersistedMapBasemap(mapProvider, mapReady);

const mapPayload = computed(() =>
  buildShipmentListMapPayload(props.shipments, {
    hiddenStatusKeys: hiddenStatusKeys.value,
  }),
);

const shipmentsById = computed(() => buildShipmentsById(props.shipments));

const interactionConfig = computed(() => {
  const list = createShipmentListMapInteractions(shipmentsById.value, router);
  const convoy = convoyInteractionFragment.value;
  return {
    ...list,
    triggers: convoy.triggers,
    onHit(event: Parameters<typeof convoy.onHit>[0]) {
      const before = expandedShipmentIds.value;
      convoy.onHit(event);
      if (expandedShipmentIds.value !== before) return;
      return list.onHit(event);
    },
    surface(event: Parameters<NonNullable<typeof convoy.surface>>[0]) {
      const convoySurface = convoy.surface?.(event);
      if (convoySurface) return convoySurface;
      return list.surface?.(event) ?? null;
    },
  };
});

const {
  lastHoverHit,
  screenPoint,
  reapply: reapplyMapInteractions,
} = useMapInteractions(mapProvider, interactionConfig);

const groupHoverTrackers = computed(() =>
  groupHoverTrackersFromHit(lastHoverHit.value),
);

const allContentBounds = computed<LatLngBounds | null>(() => {
  const { boundsCoords } = mapPayload.value;
  const convoyCoords: [number, number][] = convoyMarkers.value.map((m) => [
    m.position.lng,
    m.position.lat,
  ]);
  const coords = [...boundsCoords, ...convoyCoords];
  if (!coords.length) return null;
  return latLngBoundsFromLngLatCoordinates(coords);
});

function toggleLegendStatus(statusKey: string) {
  const next = new Set(hiddenStatusKeys.value);
  if (next.has(statusKey)) {
    next.delete(statusKey);
  } else {
    next.add(statusKey);
  }
  hiddenStatusKeys.value = next;
}

function paintLayers() {
  if (!mapReady.value || !layersPaintEnabled.value) return;

  const { markers } = mapPayload.value;

  const endpointsLayer = mapProvider.addLayer(LAYER_ENDPOINTS_ID);
  if (markers.length) {
    endpointsLayer.setMarkers(markers, ENDPOINT_CLUSTER_OPTIONS);
  } else {
    endpointsLayer.clear();
  }

  const live = livePaint.value;
  const convoyLayer = mapProvider.addLayer(LAYER_CONVOY_ID);
  if (live.markers.length) {
    convoyLayer.setMarkers(live.markers);
  } else {
    convoyLayer.clear();
  }
  const spiderfyLayer = mapProvider.addLayer(LAYER_CONVOY_SPIDERFY_ID);
  if (live.spiderfyLegs.features.length) {
    spiderfyLayer.setGeoJSON(live.spiderfyLegs);
  } else {
    spiderfyLayer.clear();
  }
}

function tryAutoFitToContentBounds(bounds: LatLngBounds) {
  if (userAdjustedMapView.value) return;
  treatNextMoveEndAsProgrammatic.value = true;
  mapProvider.fitBounds(bounds);
  window.setTimeout(() => {
    if (treatNextMoveEndAsProgrammatic.value) {
      treatNextMoveEndAsProgrammatic.value = false;
    }
    mapMoveIntentTrackingEnabled.value = true;
  }, 500);
}

function schedulePaint() {
  void afterNextBrowserPaint().then(() => {
    paintLayers();
    reapplyMapInteractions();
    void nextTick(() => {
      const bounds = allContentBounds.value;
      if (bounds) {
        tryAutoFitToContentBounds(bounds);
      } else if (!props.loading) {
        mapMoveIntentTrackingEnabled.value = true;
      }
    });
  });
}

watch(
  () =>
    [
      props.shipments,
      hiddenStatusKeys.value,
      positionsQuery.data.value,
      [...expandedShipmentIds.value].join(','),
    ] as const,
  () => {
    schedulePaint();
  },
  { deep: true },
);

watch(
  () => props.shipments.map((s) => s.id).join(','),
  () => {
    userAdjustedMapView.value = false;
    mapMoveIntentTrackingEnabled.value = false;
    resetExpandedShipments();
  },
);

function onMapReady() {
  offMapMoveEnd = mapProvider.onMoveEnd(() => {
    if (treatNextMoveEndAsProgrammatic.value) {
      treatNextMoveEndAsProgrammatic.value = false;
      mapMoveIntentTrackingEnabled.value = true;
      return;
    }
    if (!mapMoveIntentTrackingEnabled.value) return;
    userAdjustedMapView.value = true;
  });
  mapReady.value = true;
  layersPaintEnabled.value = false;
  void afterNextBrowserPaint().then(() => {
    layersPaintEnabled.value = true;
    schedulePaint();
  });
}

onBeforeUnmount(() => {
  offMapMoveEnd?.();
  offMapMoveEnd = undefined;
});
</script>

<template>
  <div
    class="relative flex size-full min-h-0 flex-1 flex-col"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <div
      v-if="loading"
      class="pointer-events-auto absolute inset-0 z-[500] flex cursor-wait items-center justify-center rounded-md bg-white/80 backdrop-blur-[1px]"
      role="status"
      aria-label="Loading map"
    >
      <ProgressSpinner
        :stroke-width="PVT_SPINNER_STROKE_WIDTH"
        :style="PVT_SPINNER_STYLE_OVERLAY"
        aria-hidden="true"
      />
    </div>
    <div class="relative min-h-0 flex-1">
      <GpxMap
        class="size-full min-h-0"
        :map-provider="mapProvider"
        @ready="onMapReady"
      >
        <template #top-right>
          <GpxMapBasemapToggle v-model="basemapMode" />
        </template>
        <template #right-center>
          <div class="flex flex-col items-end gap-1">
            <GpxMapZoomControl :max-zoom="20" />
            <GpxMapFitBoundsControl
              v-if="allContentBounds"
              :bounds="allContentBounds"
              button-title="Fit all map content"
              aria-label="Fit all map content"
            />
          </div>
        </template>
      </GpxMap>
      <ConvoyGroupHoverOverlay
        :trackers="groupHoverTrackers"
        :screen-point="screenPoint"
      />
    </div>
    <div class="pointer-events-none absolute inset-0 z-10">
      <div
        class="pointer-events-auto absolute bottom-3 left-3 right-14 flex justify-start"
      >
        <ShipmentListMapLegend
          :hidden-status-keys="hiddenStatusKeys"
          @toggle-status="toggleLegendStatus"
        />
      </div>
    </div>
  </div>
</template>
